"use client";

import Image from "next/image";
import React from "react";

import { useImageWithFallback } from "@/hooks/useImageWithFallback";
import { OptimizedImageObject } from "@/types/image.types";
import { cn } from "@/utils/cn";

interface SimpleGalleryProps {
  images: (string | OptimizedImageObject)[];
  className?: string;
  alt?: string;
}

// Individual image component with fallback support
const GalleryImage: React.FC<{
  image: string | OptimizedImageObject;
  alt: string;
  className?: string;
  priority?: boolean;
}> = ({ image, alt, className, priority = false }) => {
  const { src, hasFailed } = useImageWithFallback(image, {
    preferredSize: "large",
  });

  if (hasFailed || !src || src.trim() === "") {
    return (
      <div
        className={cn(
          "bg-gray-100 flex items-center justify-center",
          className,
        )}
      >
        <div className="text-gray-400 text-sm">No image</div>
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      fill
      className="object-cover"
      priority={priority}
      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
    />
  );
};

export const SimpleGallery: React.FC<SimpleGalleryProps> = ({
  images,
  className,
  alt = "Property image",
}) => {
  // If no images, don't render the component
  if (!images || images.length === 0) {
    return null;
  }

  // Take first 4 images
  const galleryImages = images.slice(0, 4);
  const mainImage = galleryImages[0];
  const miniImages = galleryImages.slice(1, 4); // Take up to 3 mini images

  return (
    <div className={cn("grid grid-cols-3 gap-4", className)}>
      {/* Main photo - spans full left column */}
      <div className="col-span-3 relative aspect-[4/3] rounded overflow-hidden">
        <GalleryImage
          image={mainImage}
          alt={`${alt} - main`}
          priority={true}
        />
      </div>

      {/* Mini photos - right column, stacked vertically */}
      {miniImages.map((image, index) => (
        <div
          key={index}
          className={"relative aspect-[4/3] rounded overflow-hidden"}
        >
          <GalleryImage
            image={image}
            alt={`${alt} - ${index + 2}`}
          />
        </div>
      ))}
    </div>
  );
};
